import * as React from 'react';

import { Environment, ViewMode } from '@40seas/entities/common';
import { getTenantDisplayName } from '@40seas/entities/tenants';
import { UserRole } from '@40seas/entities/users';
import { AppStore, AppStoreContext } from '@40seas/frontend/dashboard/contexts';
import { Resources } from '@40seas/frontend/dashboard/providers';
import { DisplayOrganization, DisplayTenant } from '@40seas/frontend/libs/entities';
import { useSetupData, useUser } from '@40seas/frontend/libs/hooks';
import { useFeatureToggleClient } from '@40seas/frontend/libs/providers';
import { BusinessType } from '@40seas/models';
import { organizationSlugProvider } from '@40seas/shared/organization';
import jwt_decode from 'jwt-decode';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationListener } from '../hooks/useNavigationListener';
import { isGuestRole } from '@40seas/frontend/libs/hooks';

const getOrganization = (organizations: DisplayOrganization[], organizationName: string, urlRole: UserRole | null): DisplayOrganization => {
    const organization = organizations.find(({ name }) => name?.toLocaleLowerCase() === organizationName?.toLocaleLowerCase());
    if (!organization) {
        return organizations.sort((a, b) => a.name.localeCompare(b.name))[0];
    } else if (!urlRole || organization?.types?.length === 1) {
        return organization;
    } else if ((urlRole === UserRole.Supplier && organization.linkedBuyer) || (urlRole === UserRole.Buyer && !organization.linkedBuyer)) {
        return organization;
    } else {
        const otherOrganization = organizations.find(
            ({ name, id }) => name?.toLocaleLowerCase() === organizationName?.toLocaleLowerCase() && id !== organization?.id,
        );
        return otherOrganization || organization;
    }
};

export const useCustomOrganizations = () => {
    const [customOrganizations, setOrganizations] = React.useState<DisplayOrganization[]>([]);

    const user = useUser();

    const appendOrganization = (organization: DisplayOrganization) => {
        if (
            !customOrganizations.some((org) => org.id === organization.id) &&
            !user.organizations.some((org) => org.id === organization.id)
        ) {
            setOrganizations([...customOrganizations, organization]);
        }
    };

    return { customOrganizations, appendOrganization };
};

const getParams = (
    organizations: DisplayOrganization[],
): {
    organization: DisplayOrganization;
    urlRole: UserRole | null;
    tenant: DisplayTenant;
    baseName: string;
    environment: Environment | undefined;
} => {
    const params = window.location.pathname.split('/');

    const [_, organizationName, tenantFromUrl] = params;

    const searchParams = new URLSearchParams(window.location.search);

    const roleFromQuery = searchParams.get('role');

    const tokenFromQuery = searchParams.get('t');

    let environment = undefined;
    if (tokenFromQuery) {
        const decodedToken = jwt_decode<{ role: UserRole }>(tokenFromQuery);
        environment = decodedToken['environment'];
    }

    const urlRole =
        roleFromQuery === 'admin'
            ? UserRole.Factor
            : Object.values(UserRole).some((currentRole) => roleFromQuery === currentRole)
            ? (roleFromQuery as UserRole)
            : null;

    const organization = getOrganization(organizations, organizationName, urlRole);

    const tenants = organization?.tenants?.sort((a, b) => a.tenantId.localeCompare(b.tenantId));

    const selectedTenantFromUrl = tenants?.find(
        ({ tenantId }) => getTenantDisplayName(tenantId, organizationName)?.toLocaleLowerCase() === tenantFromUrl?.toLocaleLowerCase(),
    );

    const tenant =
        selectedTenantFromUrl ||
        tenants?.find((a) => a.tenantId.toLowerCase() === organization.name.toLocaleLowerCase()) ||
        tenants?.find(({ environment }) => environment === Environment.Live) ||
        tenants?.[0];

    const baseName = organizationSlugProvider.get({
        organizationName: organization?.name,
        tenant: tenant?.tenantId,
        viewMode: tenant?.viewMode,
    });

    return { organization, tenant, urlRole, baseName, environment };
};

export const AppStoreContextProvider = ({ children }: { children: React.ReactNode }) => {
    const user = useUser();
    const setup = useSetupData();
    const navigate = useNavigate();
    const location = useLocation();
    const featureToggleClient = useFeatureToggleClient();
    const { customOrganizations, appendOrganization } = useCustomOrganizations();

    const [{ organization, tenant, urlRole, baseName, environment }, setState] = useState<{
        organization: DisplayOrganization;
        tenant: DisplayTenant;
        urlRole: UserRole | null;
        baseName: string;
        environment?: Environment | undefined;
    }>(getParams([...(user?.organizations ?? []), ...customOrganizations]));

    const setOrganization = async (
        currentOrganization: DisplayOrganization,
        resource?: string,
        tenant?: string,
        navigation: boolean = true,
    ) => {
        const currentTenant =
            currentOrganization.tenants.find(({ tenantId, environment }) =>
                tenant ? tenantId === tenant : environment === Environment.Live,
            ) || currentOrganization.tenants[0];

        const newBaseName = organizationSlugProvider.get({
            organizationName: currentOrganization.name,
            tenant: currentTenant.tenantId,
            viewMode: currentTenant.viewMode,
        });

        const roleOverride = urlRole && (currentTenant.viewMode === ViewMode.Buyer ? UserRole.Buyer : UserRole.Supplier);

        appendOrganization(currentOrganization);

        setState({ organization: currentOrganization, tenant: currentTenant, urlRole: roleOverride, baseName: newBaseName });

        if (navigation) {
            navigate({
                pathname: `${newBaseName}${resource !== undefined ? `/${resource}` : getResource()}`,
            });
        }
    };

    const setLogo = (logo: string) => {
        setState((prevState) => ({ ...prevState, organization: { ...prevState.organization, logo } }));
    };

    const setTenant = (currentTenant: DisplayTenant): void => {
        const newBaseName = organizationSlugProvider.get({
            organizationName: organization.name,
            tenant: currentTenant.tenantId,
            viewMode: tenant.viewMode,
        });

        setState({ organization, tenant: currentTenant, urlRole, baseName: newBaseName });

        navigate({
            pathname: `${newBaseName}${getResource()}`,
            search: location.search,
        });
    };

    const getResource = (): string => {
        const tenant = baseName.replace('/', '\\/');
        const regex = new RegExp(`${tenant}/([^/]*)`);
        const match = location.pathname.match(regex);

        const resourceList = Object.values(Resources);

        if (match) {
            const resource = resourceList.find((res) => res === match[1]);
            if (resource) {
                return `/${resource}`;
            }
        }

        const segments = location.pathname
            .replace(new RegExp(`^${tenant}/`), '')
            .split('/')
            .filter(Boolean);
        if (segments.length >= 2) {
            const resource = resourceList.find((res) => res === `${segments[0]}/${segments[1]}`);
            if (resource) {
                return `/${resource}`;
            }
        }

        return '';
    };

    React.useEffect(() => {
        if (window.location.pathname === '/') {
            navigate({
                // TODO : Hard coded solution for now, navigating to buyers as default
                pathname: `${baseName + ([urlRole, tenant?.viewMode].includes(UserRole.Buyer) ? '/buyers' : '')}${getResource()}`,
                search: location.search,
            });
        }

        const currenctOrganizationSlug = window.location.pathname.toLowerCase().split('/')[1];
        if (
            organization &&
            !currenctOrganizationSlug.startsWith(organization.name) &&
            organization.types?.length === 1 &&
            organization.types?.includes(BusinessType.Buyer)
        ) {
            const supplierSlug =
                organization.tenants[0].environment === Environment.Live
                    ? organization.tenants[0].tenantId.toLowerCase()
                    : organization.tenants[0].tenantId.split('-SANDBOX')[0].toLowerCase();

            if (currenctOrganizationSlug.toLowerCase().startsWith(supplierSlug)) {
                navigate({
                    pathname: organization.name,
                    search: location.search,
                });
            }
        }
    }, []);

    useNavigationListener(() => {
        const newParams = getParams([...user.organizations, ...customOrganizations]);
        if (organization !== newParams.organization || tenant !== newParams.tenant) setState(newParams);
    });

    useEffect(() => {
        // Check if setup is defined
        if (setup) {
            const fetchFeatureToggleProperties = async () => {
                featureToggleClient.identify({
                    key: 'dashboard',
                    email: user.email,
                    name: user.fullName,
                    id: user.auth0Id,
                    tenantId: tenant?.tenantId,
                    organization: organization?.id,
                });
            };

            // Call the fetch function only once
            void fetchFeatureToggleProperties();
        }
    }, [organization, tenant]);

    const appStoreContext: AppStore = {
        organization,
        isProductionEnv: !setup.urls.API_URL.includes('http://localhost'),
        isTestMode: environment === Environment.Sandbox || tenant?.environment === Environment.Sandbox,
        basename: baseName,
        urlRole: urlRole,
        viewMode:
            isGuestRole(user.role as UserRole) || isGuestRole(urlRole as UserRole) || urlRole === UserRole.Buyer
                ? ViewMode.Buyer
                : tenant?.viewMode,
        business: { id: tenant?.businessId as string },
        tenant: tenant?.tenantId,
        setOrganization,
        setTenant,
        setLogo,
    };

    return <AppStoreContext.Provider value={appStoreContext}>{children}</AppStoreContext.Provider>;
};
